
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Plus, Lock, Coins, Archive } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAccount, useConnect } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useIsMobile } from "@/hooks/use-mobile";
import { useCheckDAOMembership } from "@/hooks/useCheckDAOMembership";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CreateCoinButton = () => {
  const navigate = useNavigate();
  const { isConnected } = useAccount();
  const { connect, connectors } = useConnect();
  const { open } = useWeb3Modal();
  const isMobile = useIsMobile();
  const { isDAOMember, isLoading } = useCheckDAOMembership();
  const { toast } = useToast();
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  const handleStartNewCoin = () => {
    setShowCreateMenu(false);
    if (!isConnected) {
      localStorage.removeItem('wallet_manually_disconnected');
      localStorage.setItem('wallet_connect_requested', 'true');
      const injectedConnector = connectors.find((item) => item.type === 'injected');
      if (injectedConnector) connect({ connector: injectedConnector });
      else open();
      return;
    }
    
    if (!isDAOMember) {
      toast({
        title: "DAO Membership Required",
        description: "You must create a DAO first before launching a token. Visit DAO It to get started.",
        variant: "destructive",
      });
      navigate('/dao-it');
      return;
    }
    
    navigate('/create-coin');
  };

  const handleCreateInception = () => {
    setShowCreateMenu(false);
    navigate('/inception');
  };

  return (
    <>
      {/* Desktop version */}
      <div className="hidden sm:flex gap-2">
        <Button 
          size="sm"
          variant="outline"
          onClick={() => navigate('/dao-it')}
          className="hidden sm:flex"
        >
          DAO It
        </Button>
        
        <Button 
          size="sm"
          onClick={() => setShowCreateMenu(true)}
          className="hidden sm:flex"
          disabled={isLoading}
        >
          {isConnected && !isDAOMember && !isLoading && <Lock className="mr-2 h-4 w-4" />}
          Start a New Coin
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
      
      {/* Mobile version */}
      <Button 
        size="sm"
        variant="outline"
        onClick={() => setShowCreateMenu(true)}
        className="sm:hidden flex items-center"
        disabled={isLoading}
      >
        {isConnected && !isDAOMember && !isLoading ? <Lock className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </Button>

      <Dialog open={showCreateMenu} onOpenChange={setShowCreateMenu}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>What would you like to create?</DialogTitle>
            <DialogDescription>
              Launch a community coin or mint a smart-contract collection vault on Polygon.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 pt-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleStartNewCoin}
              className="rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
            >
              <Coins className="mb-3 h-6 w-6 text-primary" />
              <span className="block font-medium">Community coin</span>
              <span className="mt-1 block text-sm text-muted-foreground">Create a fair-launch token for your DAO.</span>
              <span className="mt-3 flex items-center text-sm text-primary">Start a new coin <ArrowRight className="ml-1 h-4 w-4" /></span>
            </button>
            <button
              type="button"
              onClick={handleCreateInception}
              className="rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
            >
              <Archive className="mb-3 h-6 w-6 text-primary" />
              <span className="block font-medium">Inception Token</span>
              <span className="mt-1 block text-sm text-muted-foreground">Mint an ownership token and manage its asset vault.</span>
              <span className="mt-3 flex items-center text-sm text-primary">Open Inception creation <ArrowRight className="ml-1 h-4 w-4" /></span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
